export type NavGroupId = "quotes" | "shipments" | "tracking" | "carriers" | "templates" | "addresses" | "analytics" | "account" | "dispatch" | "fleet" | "exceptions";
export type NavItemIconId =
  | "quoteLtl"
  | "quoteParcel"
  | "quoteRetrieve"
  | "shipmentList"
  | "shipmentBook"
  | "shipmentCancel"
  | "trackingSearch"
  | "trackingRecent"
  | "carrierList"
  | "templateList"
  | "addressList"
  | "analyticsDashboard"
  | "accountKeys"
  | "accountTeam"
  | "accountBilling"
  | "accountSettings"
  | "accountIntegrations"
  | "analyticsReports"
  | "dispatchConsole"
  | "dispatchNewLoad"
  | "fleetRegistry"
  | "exceptionsQueue";

export type NavSection = "operations" | "rate_shopping" | "tools" | "account";

export type NavItem = {
  path: string;
  label: string;
  icon: NavItemIconId;
};

export type NavGroup = {
  id: NavGroupId;
  label: string;
  basePath: string;
  section: NavSection;
  items: NavItem[];
};

export const NAV_GROUPS: NavGroup[] = [
  // ── Operations ───────────────────────────────────────────────────────────
  {
    id: "dispatch",
    label: "Dispatch",
    basePath: "/dispatch",
    section: "operations",
    items: [
      { path: "/dispatch", label: "Dispatch Console", icon: "dispatchConsole" },
      { path: "/dispatch/loads/new", label: "New Load", icon: "dispatchNewLoad" },
    ],
  },
  {
    id: "fleet",
    label: "Fleet",
    basePath: "/fleet",
    section: "operations",
    items: [
      { path: "/fleet", label: "Fleet Registry", icon: "fleetRegistry" },
    ],
  },
  {
    id: "carriers",
    label: "Carriers",
    basePath: "/carriers",
    section: "operations",
    items: [{ path: "/carriers", label: "Connected Carriers", icon: "carrierList" }],
  },
  {
    id: "exceptions",
    label: "Exceptions",
    basePath: "/exceptions",
    section: "operations",
    items: [
      { path: "/exceptions", label: "Exception Queue", icon: "exceptionsQueue" },
    ],
  },

  // ── Rate Shopping ────────────────────────────────────────────────────────
  {
    id: "quotes",
    label: "Quotes",
    basePath: "/quotes",
    section: "rate_shopping",
    items: [
      { path: "/quotes", label: "LTL Quote", icon: "quoteLtl" },
      { path: "/quotes?mode=parcel", label: "Parcel Quote", icon: "quoteParcel" },
      { path: "/quotes/retrieve", label: "Retrieve Quote", icon: "quoteRetrieve" },
    ],
  },
  {
    id: "shipments",
    label: "Shipments",
    basePath: "/shipments",
    section: "rate_shopping",
    items: [
      { path: "/shipments", label: "All Shipments", icon: "shipmentList" },
      { path: "/shipments/book", label: "Book Shipment", icon: "shipmentBook" },
      { path: "/shipments/cancel", label: "Cancel Shipment", icon: "shipmentCancel" },
    ],
  },
  {
    id: "tracking",
    label: "Tracking",
    basePath: "/tracking",
    section: "rate_shopping",
    items: [
      { path: "/tracking", label: "Track by PRO/Tracking", icon: "trackingSearch" },
      { path: "/tracking?mode=recent", label: "Recent Events", icon: "trackingRecent" },
    ],
  },

  // ── Tools ────────────────────────────────────────────────────────────────
  {
    id: "templates",
    label: "Templates",
    basePath: "/templates",
    section: "tools",
    items: [{ path: "/templates", label: "Shipment Templates", icon: "templateList" }],
  },
  {
    id: "addresses",
    label: "Addresses",
    basePath: "/addresses",
    section: "tools",
    items: [{ path: "/addresses", label: "Address Book", icon: "addressList" }],
  },
  {
    id: "analytics",
    label: "Analytics",
    basePath: "/analytics",
    section: "tools",
    items: [
      { path: "/analytics", label: "Dashboard", icon: "analyticsDashboard" },
      { path: "/reports", label: "Reports", icon: "analyticsReports" },
    ],
  },

  // ── Account ──────────────────────────────────────────────────────────────
  {
    id: "account",
    label: "Account",
    basePath: "/account",
    section: "account",
    items: [
      { path: "/account/team", label: "Team", icon: "accountTeam" },
      { path: "/account/billing", label: "Billing", icon: "accountBilling" },
      { path: "/account/api-keys", label: "API Keys", icon: "accountKeys" },
      { path: "/account/settings", label: "Settings", icon: "accountSettings" },
      { path: "/account/integrations", label: "Integrations", icon: "accountIntegrations" },
    ],
  },
];

export const SECTION_LABELS: Record<NavSection, string> = {
  operations: "Operations",
  rate_shopping: "Rate Shopping",
  tools: "Tools",
  account: "Account",
};

export function getActiveGroupId(pathname: string): NavGroupId | null {
  if (pathname.startsWith("/dispatch")) return "dispatch";
  if (pathname.startsWith("/fleet")) return "fleet";
  if (pathname.startsWith("/exceptions")) return "exceptions";
  if (pathname.startsWith("/quotes")) return "quotes";
  if (pathname.startsWith("/shipments")) return "shipments";
  if (pathname.startsWith("/tracking")) return "tracking";
  if (pathname.startsWith("/carriers")) return "carriers";
  if (pathname.startsWith("/templates")) return "templates";
  if (pathname.startsWith("/addresses")) return "addresses";
  if (pathname.startsWith("/analytics")) return "analytics";
  if (pathname.startsWith("/reports")) return "analytics";
  if (pathname.startsWith("/account")) return "account";
  return null;
}

export function isNavPathActive(path: string, pathname: string, search: string): boolean {
  if (path.includes("?")) {
    return `${pathname}${search}` === path;
  }

  return pathname === path && !search;
}
